import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "flush", "scrollable", "compact"];
import { forwardRef } from 'react';
import styled from 'foundations-theming/utils';
import { useCardContext } from './CardContext';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { CARD_TRANSITION_ANIMATION_TIMING } from '../../../constants/times';
import { AccordionTransition } from '../AccordionTransition';
import { jsx as _jsx } from "react/jsx-runtime";
const duration = parseInt(CARD_TRANSITION_ANIMATION_TIMING, 10);
export const StyledCardSection = styled.div.withConfig({
  displayName: "CardSection__StyledCardSection",
  componentId: "sc-4ey7ca-0"
})(({
  $flush,
  $scrollable,
  $compact,
  $expandable,
  $cssStyles,
  theme
}) => {
  const spacing = $compact ? theme.space['600'] : theme.space['1000'];
  const paddingTop = spacing;
  const paddingBottom = $scrollable ? theme.space['0'] : spacing;
  const paddingX = $flush ? theme.space['0'] : spacing;
  const padding = `${paddingTop} ${paddingX} ${paddingBottom}`;
  const isScrollableAndExpandable = $expandable && $scrollable;
  return Object.assign({
    padding,
    borderRadius: 'inherit',
    // replaces the `has--vertical-spacing` className styling
    '& > :last-child': {
      marginBottom: theme.space['0']
    }
  }, isScrollableAndExpandable && {
    // we need to use increased specificity to override CardWrapper::Outer `& ${StyledCardSection}:last-child` declaration
    '&&&:last-child': {
      marginBottom: spacing
    }
  }, $scrollable ? {
    flex: '1 1 auto',
    overflow: 'auto',
    '&:last-child': {
      marginBottom: spacing
    },
    '& + &': {
      paddingTop: spacing
    }
  } : {
    '& + &, & + * &': {
      paddingTop: '0'
    }
  }, $cssStyles);
});
export const CardSection = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      children,
      flush = false,
      scrollable = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    compact,
    expanded,
    expandable
  } = useCardContext();
  const content = /*#__PURE__*/_jsx(StyledCardSection, Object.assign({}, domProps, {
    ref: ref,
    $flush: flush,
    $compact: compact,
    $expandable: expandable,
    $scrollable: scrollable,
    $cssStyles: cssStyles,
    children: children
  }));
  return expandable ? /*#__PURE__*/_jsx(AccordionTransition, {
    duration: duration,
    open: expanded,
    children: content
  }) : content;
});
CardSection.displayName = 'CardSection';